import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DsBoxModule, DsButtonModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { CustomerVehicle, CustomerVehicleRelationType } from '../../../customer/model/customer-vehicle.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { RelationButtonConfig } from '../customer-vehicle-relations/relation-button-config';
import { VehicleInfoComponent } from '../vehicle-info/vehicle-info.component';

@Component({
  selector: 'cdf-vehicle-box',
  imports: [DsBoxModule, DsButtonModule, DsTooltipDirectiveModule, VehicleInfoComponent],
  host: {
    '(click)': 'clickOnBox()',
    '(keydown.enter)': 'clickOnBox()',
    '(keydown.space)': 'clickOnBox()',
    '[class.has-button-column]': 'canRemoveVehicle()',
    '[class.selectable]': 'enableSelection()',
    '[class.small-image]': 'smallImage()',
    '[tabindex]': 'enableSelection() ? 0 : -1',
  },
  template: `
    <cdf-vehicle-info
      [vehicle]="vehicle()"
      [activeCountry]="activeCountry()"
      [removeButton]="removeButton()"
      [addButton]="addButton()"
      [truncateRelations]="truncateRelations()"
      [canEditLicensePlate]="canEditLicensePlate()"
      (removeRelation)="removeRelation.emit($event)"
      (addRelation)="addRelation.emit()"
      (editLicensePlate)="editLicensePlate.emit()"
    />
    @if (canRemoveVehicle()) {
      <div class="button-col">
        <button ds-button (click)="removeVehicle.emit()" icon="bin" variant="ghost-destructive"></button>
      </div>
    }
  `,
  styleUrl: './vehicle-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleBoxComponent {
  readonly i18n = useI18n();

  readonly vehicle = input.required<CustomerVehicle>();
  readonly activeCountry = input<string | undefined>();

  readonly enableSelection = input(false, { transform: booleanAttribute });
  readonly canRemoveVehicle = input(false, { transform: booleanAttribute });
  readonly canRemoveRelations = input(false, { transform: booleanAttribute });
  readonly canAddRelations = input(false, { transform: booleanAttribute });
  readonly canEditLicensePlate = input(false, { transform: booleanAttribute });

  readonly truncateRelations = input(false, { transform: booleanAttribute });

  readonly smallImage = input(false, { transform: booleanAttribute });

  readonly selectVehicle = output<void>();
  readonly removeRelation = output<CustomerVehicleRelationType>();
  readonly removeVehicle = output<void>();
  readonly addRelation = output<void>();
  readonly editLicensePlate = output<void>();

  readonly removeButton = computed((): RelationButtonConfig | undefined => {
    if (!this.canRemoveRelations()) {
      return undefined;
    }
    return {
      min: 2, // if there is only 1 relation, the vehicle should be removed instead of the relation
      tooltip: this.canRemoveVehicle()
        ? this.i18n('customerVehicleRelation.cantRemoveSingleRelationInsteadRemoveVehicle')
        : this.i18n('customerVehicleRelation.cantRemoveSingleRelation'),
    };
  });

  readonly addButton = computed((): RelationButtonConfig | undefined => {
    if (!this.canAddRelations()) {
      return undefined;
    }
    return {
      // Cannot add relations when all relation types are already added
      max: Object.values(CustomerVehicleRelationType).length - 1,
      tooltip: this.i18n('customerVehicleRelation.noRelationsToAdd'),
    };
  });

  protected clickOnBox() {
    if (this.enableSelection() && window.getSelection()?.type !== 'Range') {
      this.selectVehicle.emit();
    }
  }
}
